<template>
    <div class="recent-trips">
        <div class="recent-trips__head">
            <v-icon icon="mdi-map-marker-path" size="14" class="me-1"/>
            <span>Recent trips</span>
            <span v-if="visibleTrips.length" class="recent-trips__count">Last {{ visibleTrips.length }}</span>
            <v-progress-circular v-if="loading" indeterminate size="12" width="2" class="ms-auto"/>
        </div>

        <div v-if="!loading && !visibleTrips.length" class="recent-trips__empty">
            No trips in the last 3 days.
        </div>

        <div class="recent-trips__list">
            <template v-for="(group, gi) in grouped" :key="gi">
                <div class="recent-trips__day">{{ group.label }}</div>
                <div
                    v-for="trip in group.trips"
                    :key="trip.id"
                    class="trip-row"
                    :class="{'trip-row--open': expandedId === trip.id}"
                    @click="toggle(trip)"
                >
                    <div class="trip-row__main">
                        <v-icon :icon="modeIcon(trip)" size="16" class="trip-row__mode"/>
                        <div class="trip-row__text">
                            <div class="trip-row__label">{{ label(trip) }}</div>
                            <div class="trip-row__sub">
                                {{ startTime(trip) }} · {{ duration(trip.duration) }} · {{ distance(trip) }}
                                <span v-if="topSpeed(trip)"> · ↑{{ topSpeed(trip) }}</span>
                            </div>
                        </div>
                        <v-chip v-if="hasTripScore(trip)" size="x-small" variant="flat"
                                :color="tripScoreColor(trip.trip_score)" class="trip-row__score">
                            {{ formatTripScore(trip.trip_score) }}
                        </v-chip>
                        <v-icon :icon="expandedId === trip.id ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                size="16" class="trip-row__chev"/>
                    </div>

                    <div v-if="expandedId === trip.id" class="trip-row__replay" @click.stop>
                        <div v-if="!(trip.id in routeCache)" class="trip-row__loading">
                            <v-progress-circular indeterminate size="18" width="2"/>
                        </div>
                        <TripRouteMini v-else :routes="routeCache[trip.id]" :height="150"/>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import {send} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";
import {formatDateTime, formatDate} from "@/composables/useTimezone";
import {useTripScore} from "@/composables/useTripScore";
import TripRouteMini from "./TripRouteMini.vue";

const props = defineProps({
    device: {type: Object, required: true},
    // Only fetch when the popup is actually open (avoids a request per marker).
    active: {type: Boolean, default: false},
});

const {tripScoreColor, formatTripScore, hasTripScore} = useTripScore();

const trips = ref([]);
const loading = ref(false);
const expandedId = ref(null);
const routeCache = ref({});

// --- parsing / formatting helpers ---
const parseUtc = (ts) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? ts : String(ts).replace(' ', 'T') + 'Z');

// Show only the 2 most recent trips in the marker popup (a compact glance); the
// full history lives on the Activity screen.
const visibleTrips = computed(() => {
    return trips.value
        .filter(t => t.time)
        .sort((a, b) => parseUtc(b.time) - parseUtc(a.time))
        .slice(0, 2);
});

const grouped = computed(() => {
    const todayD = formatDate(new Date());
    const yD = formatDate(new Date(Date.now() - 864e5));
    const groups = [];
    const byKey = {};
    for (const t of visibleTrips.value) {
        const fd = formatDate(t.time);
        const label = fd === todayD ? 'Today'
            : fd === yD ? 'Yesterday'
                : formatDateTime(t.time, {weekday: 'short', month: 'short', day: 'numeric'});
        if (!byKey[label]) {
            byKey[label] = {label, trips: []};
            groups.push(byKey[label]);
        }
        byKey[label].trips.push(t);
    }
    return groups;
});

const shortAddr = (a) => {
    if (!a) return '';
    return String(a).split(',')[0].trim().slice(0, 28);
};

const label = (trip) => {
    const a = shortAddr(trip.from), b = shortAddr(trip.to);
    if (a && b) return a === b ? `Around ${a}` : `${a} → ${b}`;
    if (b) return `To ${b}`;
    if (a) return `From ${a}`;
    return 'Trip';
};

const startTime = (trip) => formatDateTime(trip.time, {hour: 'numeric', minute: '2-digit'});

const duration = (sec) => {
    const s = Number(sec) || 0;
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m`;
    return `${s}s`;
};

const distance = (trip) => strOperations().unitifyDistance(trip.distance, 'distance', 'km');
const topSpeed = (trip) => (trip.max_speed ? strOperations().unitifyDistance(trip.max_speed) : '');

const modeIcon = (trip) => {
    const s = parseFloat(strOperations().unitifyDistance(trip.max_speed)) || 0;
    if (s >= 20) return 'mdi-car';
    if (s >= 8) return 'mdi-bike';
    return 'mdi-walk';
};

// --- data ---
// Refetch every time the popup opens (SWR-style): the existing list stays
// visible until fresh data arrives, so a trip recorded since the last open shows
// up. `loading` only guards against a concurrent in-flight request.
const fetchTrips = () => {
    if (loading.value || !props.device?.asset_id) return;
    loading.value = true;
    send('get', `assets/${props.device.asset_id}/trips`, {page: 1})
        .then(r => {
            trips.value = r?.data?.data?.data || [];
        })
        .catch(() => { /* best-effort; keep the previous list */ })
        .finally(() => {
            loading.value = false;
        });
};

const toggle = (trip) => {
    if (expandedId.value === trip.id) {
        expandedId.value = null;
        return;
    }
    expandedId.value = trip.id;
    if (trip.id in routeCache.value) return; // already cached (incl. an empty result)
    send('get', `assets/${props.device.asset_id}/trips/${trip.id}/routes`)
        .then(r => {
            routeCache.value = {...routeCache.value, [trip.id]: r?.data?.data || []};
        })
        .catch(() => {
            routeCache.value = {...routeCache.value, [trip.id]: []};
        });
};

// Fetch when the popup opens; reset the expanded replay when it closes.
watch(() => props.active, (open) => {
    if (open) fetchTrips();
    else expandedId.value = null;
}, {immediate: true});

// If the popup is reused for a different device, reload.
watch(() => props.device?.asset_id, () => {
    trips.value = [];
    expandedId.value = null;
    routeCache.value = {};
    if (props.active) fetchTrips();
});
</script>

<style scoped>
.recent-trips {
    margin-top: 6px;
}

.recent-trips__head {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
}

.recent-trips__count {
    font-weight: 400;
    color: #94a3b8;
    margin-left: 6px;
    font-size: 11px;
}

.recent-trips__empty {
    font-size: 12px;
    color: #94a3b8;
    padding: 4px 0 2px;
}

.recent-trips__list {
    max-height: 260px;
    overflow-y: auto;
    margin: 0 -4px;
    padding: 0 4px;
}

.recent-trips__day {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    margin: 6px 0 2px;
}

.trip-row {
    border-radius: 8px;
    padding: 5px 6px;
    cursor: pointer;
    transition: background 0.12s;
}

.trip-row:hover, .trip-row--open {
    background: rgba(37, 99, 235, 0.06);
}

.trip-row__main {
    display: flex;
    align-items: center;
    gap: 7px;
}

.trip-row__mode {
    color: #2563eb;
    flex-shrink: 0;
}

.trip-row__text {
    min-width: 0;
    flex: 1;
}

.trip-row__label {
    font-size: 12.5px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trip-row__sub {
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trip-row__score {
    flex-shrink: 0;
    font-weight: 700;
}

.trip-row__chev {
    color: #94a3b8;
    flex-shrink: 0;
}

.trip-row__replay {
    margin-top: 6px;
}

.trip-row__loading {
    /* Match the mini-map height so the popup doesn't jump when the map appears. */
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
